const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
const API_URL = process.env.NODE_ENV === "development" ? baseUrl : `${baseUrl}/api`;
export const guttenbergApiOrigin = () => new URL(API_URL || "/", window.location.origin).href.replace(/\/$/, "");

function authHeaders() {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, init = {}) {
  const body = init.body;
  const headers =
    body instanceof FormData
      ? { ...authHeaders(), ...(init.headers || {}) }
      : { "Content-Type": "application/json", ...authHeaders(), ...(init.headers || {}) };
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    const detail = await response.text();
    const error = new Error(formatApiError(detail, response.status));
    error.status = response.status;
    try {
      const parsed = JSON.parse(detail);
      error.detail = parsed.detail ?? parsed;
    } catch {
      error.detail = detail;
    }
    throw error;
  }
  return response.status === 204 ? null : response.json();
}

function formatApiError(detail, status) {
  if (!detail) return `HTTP ${status}`;
  try {
    const parsed = JSON.parse(detail);
    if (typeof parsed.detail === "string") return parsed.detail;
    if (parsed.detail) return JSON.stringify(parsed.detail);
  } catch {
    return detail;
  }
  return detail;
}

function upload(path, fields) {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  return request(path, { method: "POST", body: form });
}

function uploadMany(path, field, files) {
  const form = new FormData();
  Array.from(files || []).forEach((file) => form.append(field, file));
  return request(path, { method: "POST", body: form });
}

function withQuery(path, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export const api = {
  copilotAccess: (id) => request(`/guttenberg/projects/${id}/copilot/access`),
  createCopilotAccess: (id, body) => request(`/guttenberg/projects/${id}/copilot/access`, { method: "POST", body: JSON.stringify(body) }),
  revokeCopilotAccess: (id, key) => request(`/guttenberg/projects/${id}/copilot/access/${key}`, { method: "DELETE" }),
  getFastMode: () => request("/guttenberg/settings/fast-mode"),
  setFastMode: (fastMode) =>
    request("/guttenberg/settings/fast-mode", { method: "PUT", body: JSON.stringify({ fast_mode: fastMode }) }),
  getAiMode: () => request("/guttenberg/settings/ai-mode"),
  setAiMode: (mode) =>
    request("/guttenberg/settings/ai-mode", { method: "PUT", body: JSON.stringify({ mode }) }),
  getAiProvider: () => request("/guttenberg/settings/provider"),
  setAiProvider: (provider) =>
    request("/guttenberg/settings/provider", { method: "PUT", body: JSON.stringify({ provider }) }),
  listProjects: (deepflowProjectId) =>
    request(`/guttenberg/projects${deepflowProjectId ? `?deepflow_project_id=${deepflowProjectId}` : ""}`),
  createProject: (payload) => request("/guttenberg/projects", { method: "POST", body: JSON.stringify(payload) }),
  deleteProject: (projectId) => request(`/guttenberg/projects/${projectId}`, { method: "DELETE" }),
  createFromDeepflow: (projectId, payload = null) =>
    request(`/guttenberg/projects/from-deepflow/${projectId}`, {
      method: "POST",
      body: payload ? JSON.stringify(payload) : undefined,
    }),
  refreshDeepflowSnapshot: (reportId) => request(`/guttenberg/projects/${reportId}/deepflow-snapshot`, { method: "POST" }),
  driveFiles: (reportId, options = {}) => {
    const params = new URLSearchParams();
    if (options.folderId) params.set("folder_id", options.folderId);
    if (options.query) params.set("q", options.query);
    if (options.recursive !== undefined) params.set("recursive", String(options.recursive));
    if (options.pageToken) params.set("page_token", options.pageToken);
    if (options.pageSize) params.set("page_size", String(options.pageSize));
    const query = params.toString();
    return request(`/guttenberg/projects/${reportId}/drive-files${query ? `?${query}` : ""}`);
  },
  importDriveFiles: (reportId, { fileIds = [], folderIds = [], recursive = true }) =>
    request(`/guttenberg/projects/${reportId}/drive-files/import`, {
      method: "POST",
      body: JSON.stringify({ file_ids: fileIds, folder_ids: folderIds, recursive }),
    }),
  getProject: (projectId) => request(`/guttenberg/projects/${projectId}`),
  updateContext: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/context`, { method: "PUT", body: JSON.stringify(payload) }),
  chat: (projectId, message) =>
    request(`/guttenberg/projects/${projectId}/chat`, { method: "POST", body: JSON.stringify({ message }) }),
  answerQuestion: (projectId, questionId, payload) =>
    request(`/guttenberg/projects/${projectId}/questions/${questionId}/answer`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  uploadFile: (projectId, file) => upload(`/guttenberg/projects/${projectId}/files`, { file }),
  uploadContextFiles: (projectId, files) =>
    uploadMany(`/guttenberg/projects/${projectId}/context/files`, "files", files),
  integrateContext: (projectId, sourceFileIds = []) =>
    request(`/guttenberg/projects/${projectId}/context/integrate`, {
      method: "POST",
      body: JSON.stringify({ source_file_ids: sourceFileIds }),
    }),
  uploadReportImage: (projectId, file, caption = "", user_label = "") =>
    upload(`/guttenberg/projects/${projectId}/assets`, { file, caption, user_label }),
  uploadContextImage: (projectId, file, caption = "", user_label = "", branchId = "") =>
    upload(`/guttenberg/projects/${projectId}/context-images`, { file, caption, user_label, branch_id: branchId }),
  uploadClientLogo: (projectId, file) => upload(`/guttenberg/projects/${projectId}/client-logo`, { file }),
  ingest: (projectId) => request(`/guttenberg/projects/${projectId}/ingest`, { method: "POST" }),
  prepareVisuals: (projectId) => request(`/guttenberg/projects/${projectId}/prepare-visuals`, { method: "POST" }),
  prepare: (projectId) => request(`/guttenberg/projects/${projectId}/prepare`, { method: "POST" }),
  resetAnalysis: (projectId) =>
    request(`/guttenberg/projects/${projectId}/analysis/reset`, { method: "POST" }),
  analysis: (projectId) => request(`/guttenberg/projects/${projectId}/analysis`),
  costs: (projectId) => request(`/guttenberg/projects/${projectId}/costs`),
  updateAnalysis: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/analysis`, { method: "PUT", body: JSON.stringify(payload) }),
  sourcePreview: (projectId, sourceId) => request(`/guttenberg/projects/${projectId}/sources/${sourceId}/preview`),
  renderChart: (projectId, spec, sectionId = "") =>
    request(`/guttenberg/projects/${projectId}/charts/render`, {
      method: "POST",
      body: JSON.stringify({ spec, section_id: sectionId }),
    }),
  updateVisualAsset: (projectId, assetId, payload) =>
    request(`/guttenberg/projects/${projectId}/analysis/visual-assets/${assetId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  outline: (projectId) => request(`/guttenberg/projects/${projectId}/generate-outline`, { method: "POST" }),
  updateOutline: (projectId, sections) =>
    request(`/guttenberg/projects/${projectId}/outline`, { method: "PUT", body: JSON.stringify({ sections }) }),
  refineOutline: (projectId, instruction) =>
    request(`/guttenberg/projects/${projectId}/outline/refine`, {
      method: "POST",
      body: JSON.stringify({ instruction }),
    }),
  approveOutline: (projectId, approved = true) =>
    request(`/guttenberg/projects/${projectId}/outline/approve`, {
      method: "POST",
      body: JSON.stringify({ approved }),
    }),
  generate: (projectId) => request(`/guttenberg/projects/${projectId}/generate-report`, { method: "POST" }),
  runReview: (projectId) => request(`/guttenberg/projects/${projectId}/review/run`, { method: "POST" }),
  job: (projectId, jobId) => request(`/guttenberg/projects/${projectId}/jobs/${jobId}`),
  cancelJob: (projectId, jobId) => request(`/guttenberg/projects/${projectId}/jobs/${jobId}/cancel`, { method: "POST" }),
  report: (projectId, branchId = "") =>
    request(withQuery(`/guttenberg/projects/${projectId}/report`, { branch_id: branchId })),
  updateReportPresentation: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/report/presentation`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  updateSection: (projectId, sectionId, markdown, origin = "", note = "", branchId = "") =>
    request(`/guttenberg/projects/${projectId}/sections/${sectionId}`, {
      method: "PUT",
      body: JSON.stringify({ markdown, origin, note, branch_id: branchId }),
    }),
  createSection: (projectId, title, afterSectionId = "", branchId = "") =>
    request(`/guttenberg/projects/${projectId}/sections`, {
      method: "POST",
      body: JSON.stringify({ title, after_section_id: afterSectionId, branch_id: branchId }),
    }),
  deleteSection: (projectId, sectionId, branchId = "") =>
    request(`/guttenberg/projects/${projectId}/sections/${sectionId}`, {
      method: "DELETE",
      body: JSON.stringify({ branch_id: branchId }),
    }),
  collaboration: (projectId) => request(`/guttenberg/projects/${projectId}/collaboration`),
  collaborationHeartbeat: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/collaboration/heartbeat`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  collaborationLeave: (projectId, sessionId) =>
    request(`/guttenberg/projects/${projectId}/collaboration/${sessionId}`, { method: "DELETE" }),
  branches: (projectId, activeBranchId = "") =>
    request(withQuery(`/guttenberg/projects/${projectId}/branches`, { active_branch_id: activeBranchId })),
  createBranch: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/branches`, { method: "POST", body: JSON.stringify(payload) }),
  switchBranch: (projectId, branchId) =>
    request(`/guttenberg/projects/${projectId}/branches/${branchId}/switch`, { method: "POST" }),
  mergeBranch: (projectId, branchId, strategy = "safe", targetBranchId = "") =>
    request(`/guttenberg/projects/${projectId}/branches/${branchId}/merge`, {
      method: "POST",
      body: JSON.stringify({ strategy, target_branch_id: targetBranchId }),
    }),
  insertSectionImage: (projectId, sectionId, file, caption, blockIndex, userLabel = "", replacePath = "", branchId = "") =>
    upload(`/guttenberg/projects/${projectId}/sections/${sectionId}/images`, {
      file,
      caption,
      user_label: userLabel,
      replace_path: replacePath,
      block_index: blockIndex,
      branch_id: branchId,
    }),
  applySectionImage: (projectId, sectionId, payload) =>
    request(`/guttenberg/projects/${projectId}/sections/${sectionId}/image-actions`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createMilestone: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/versions/milestones`, { method: "POST", body: JSON.stringify(payload) }),
  restoreMilestone: (projectId, milestoneId) =>
    request(`/guttenberg/projects/${projectId}/versions/milestones/${milestoneId}/restore`, { method: "POST" }),
  restoreSectionVersion: (projectId, sectionId, versionId) =>
    request(`/guttenberg/projects/${projectId}/versions/sections/${sectionId}/restore`, {
      method: "POST",
      body: JSON.stringify({ version_id: versionId }),
    }),
  restoreBlockVersion: (projectId, versionId, blockId) =>
    request(`/guttenberg/projects/${projectId}/versions/blocks/restore`, {
      method: "POST",
      body: JSON.stringify({ version_id: versionId, block_id: blockId }),
    }),
  reviseSection: (projectId, sectionId, instruction, branchId = "") =>
    request(`/guttenberg/projects/${projectId}/revise-section/${sectionId}`, {
      method: "POST",
      body: JSON.stringify({ instruction, branch_id: branchId }),
    }),
  createComment: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/comments`, { method: "POST", body: JSON.stringify(payload) }),
  updateCommentStatus: (projectId, commentId, status, branchId = "") =>
    request(`/guttenberg/projects/${projectId}/comments/${commentId}`, {
      method: "PATCH",
      body: JSON.stringify({ status, branch_id: branchId }),
    }),
  replyComment: (projectId, commentId, content, branchId = "") =>
    request(`/guttenberg/projects/${projectId}/comments/${commentId}/replies`, {
      method: "POST",
      body: JSON.stringify({ content, branch_id: branchId }),
    }),
  suggestComment: (projectId, commentId, branchId = "") =>
    request(withQuery(`/guttenberg/projects/${projectId}/comments/${commentId}/suggest`, { branch_id: branchId }), { method: "POST" }),
  acceptCommentSuggestion: (projectId, commentId, branchId = "") =>
    request(withQuery(`/guttenberg/projects/${projectId}/comments/${commentId}/accept`, { branch_id: branchId }), { method: "POST" }),
  rejectCommentSuggestion: (projectId, commentId, branchId = "") =>
    request(withQuery(`/guttenberg/projects/${projectId}/comments/${commentId}/reject`, { branch_id: branchId }), { method: "POST" }),
  applyComments: (projectId, payload) =>
    request(`/guttenberg/projects/${projectId}/comments/apply`, { method: "POST", body: JSON.stringify(payload) }),
  applyReportInstruction: (projectId, instruction, attachmentIds = [], bankImagePaths = [], sourceFileIds = [], branchId = "", options = {}) =>
    request(`/guttenberg/projects/${projectId}/report/instructions`, {
      method: "POST",
      body: JSON.stringify({
        instruction,
        instructions: Array.isArray(options.instructions) ? options.instructions : [],
        target_section_ids: Array.isArray(options.targetSectionIds) ? options.targetSectionIds : [],
        attachment_ids: attachmentIds,
        bank_image_paths: bankImagePaths,
        source_file_ids: sourceFileIds,
        branch_id: branchId,
        assistant_thread_id: options.assistantThreadId || "",
        user_prompt: options.userPrompt || "",
        create_restore_milestone: Boolean(options.createRestoreMilestone),
        change_scope: options.changeScope === "structure" ? "structure" : "content",
      }),
    }),
  createReportAssistantThread: (projectId, title = "") =>
    request(`/guttenberg/projects/${projectId}/report/assistant/threads`, {
      method: "POST",
      body: JSON.stringify({ title }),
    }),
  resetReportAssistant: (projectId) =>
    request(`/guttenberg/projects/${projectId}/report/assistant`, { method: "DELETE" }),
  reportAssistantMessage: (projectId, message, attachmentIds = [], bankImagePaths = [], sourceFileIds = [], threadId = "", branchId = "") =>
    request(`/guttenberg/projects/${projectId}/report/assistant`, {
      method: "POST",
      body: JSON.stringify({
        message,
        attachment_ids: attachmentIds,
        bank_image_paths: bankImagePaths,
        source_file_ids: sourceFileIds,
        thread_id: threadId,
        branch_id: branchId,
      }),
    }),
  exportPdf: (projectId, options = {}) =>
    request(`/guttenberg/projects/${projectId}/export/pdf`, {
      method: "POST",
      body: Object.keys(options).length ? JSON.stringify(options) : undefined,
    }),
  exportDocx: (projectId, options = {}) =>
    request(`/guttenberg/projects/${projectId}/export/docx`, {
      method: "POST",
      body: Object.keys(options).length ? JSON.stringify(options) : undefined,
    }),
  exportUrl: (projectId, filename) => `${API_URL}/guttenberg/files/projects/${projectId}/exports/${filename}`,
  sourceFileUrl: (projectId, sourceId) => `${API_URL}/guttenberg/files/projects/${projectId}/sources/${sourceId}`,
  assetUrl: (projectId, filename) => `${API_URL}/guttenberg/files/projects/${projectId}/assets/${filename}`,
  assetThumbnailUrl: (projectId, filename, size = 480, version = "") =>
    withQuery(`${API_URL}/guttenberg/files/projects/${projectId}/thumbnails/${filename}`, {
      size,
      v: version,
    }),
  contextAssetUrl: (projectId, filename) => `${API_URL}/guttenberg/files/projects/${projectId}/context-assets/${filename}`,
  innowideCoverLogoUrl: () => "/innowide_cover_logo.jpg",
  innowideHeaderLogoUrl: () => "/innowide_header_logo.png",
  innowideFontUrl: (filename) => `${API_URL}/guttenberg/files/projects/fonts/${filename}`,
  eventUrl: (projectId) => `${API_URL}/guttenberg/projects/${projectId}/events`,
};

export function streamGuttenbergEvents(projectId, onMessage, onError, onConnect) {
  let closed = false;
  let controller = null;
  let retryTimer = null;
  let retryAttempt = 0;
  let connectedOnce = false;

  async function connect() {
    if (closed) return;
    controller = new AbortController();
    try {
      const response = await fetch(api.eventUrl(projectId), {
        headers: authHeaders(),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error(`Flux Guttenberg HTTP ${response.status}`);
      const reconnected = connectedOnce;
      connectedOnce = true;
      retryAttempt = 0;
      onConnect?.({ reconnected });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split(/\n\n/);
        buffer = chunks.pop() || "";
        chunks.forEach((chunk) => {
          const data = chunk
            .split(/\n/)
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim())
            .join("\n");
          if (!data) return;
          try {
            onMessage(JSON.parse(data));
          } catch (error) {
            onError?.(error);
          }
        });
      }
      if (!closed) throw new Error("Flux Guttenberg ferme par le serveur");
    } catch (error) {
      if (!closed && error.name !== "AbortError") onError?.(error);
    } finally {
      controller = null;
    }
    if (!closed) {
      const delay = Math.min(30000, 1000 * (2 ** retryAttempt));
      retryAttempt += 1;
      retryTimer = window.setTimeout(connect, delay);
    }
  }

  connect();
  return {
    close() {
      closed = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      controller?.abort();
    },
  };
}
